// Estacionalidad: mes del año, mapa de calor año × mes y día de la semana.

import { useMemo, useState } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';
import type { HistoricoReservasResult } from '@/lib/types';
import { useIsDarkMode } from '@/hooks/useIsDarkMode';
import { Tarjeta, Conmutador, MESES_CORTOS, DIAS_CORTOS, fmtMoney, fmtNum, fmtPct, useTemaGraficas } from './comun';

type Metrica = 'noches' | 'reservas' | 'facturacion' | 'adr' | 'tasaCancelacion';

const METRICAS: { valor: Metrica; label: string }[] = [
  { valor: 'noches', label: 'Noches' },
  { valor: 'reservas', label: 'Reservas' },
  { valor: 'facturacion', label: 'Facturación' },
  { valor: 'adr', label: 'ADR' },
  { valor: 'tasaCancelacion', label: 'Cancelación' },
];

function formato(metrica: Metrica, v: number): string {
  if (metrica === 'facturacion' || metrica === 'adr') return fmtMoney(v);
  if (metrica === 'tasaCancelacion') return fmtPct(v);
  return fmtNum(v);
}

export default function SeccionEstacionalidad({ data }: { data: HistoricoReservasResult }) {
  const tema = useTemaGraficas();
  const isDark = useIsDarkMode();
  const [metrica, setMetrica] = useState<Metrica>('noches');
  const [calor, setCalor] = useState<'noches' | 'reservas'>('noches');

  const porMes = useMemo(
    () => data.porMes.map((m) => ({ mes: MESES_CORTOS[m.mes - 1], valor: metrica === 'tasaCancelacion' ? Number(m[metrica].toFixed(1)) : Math.round(m[metrica]) })),
    [data, metrica],
  );

  const maxCalor = useMemo(() => Math.max(1, ...data.mapaCalor.flatMap((f) => f[calor])), [data, calor]);

  const diaSemana = useMemo(
    () => data.diaSemana.map((d) => ({ dia: DIAS_CORTOS[d.dia], Entradas: d.entradas, Salidas: d.salidas, Noches: d.noches })),
    [data],
  );

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Tarjeta
          titulo="Estacionalidad"
          info="Todos los años juntos, por mes. Las noches se cuentan en el mes en que se duermen (una estancia que cruza de julio a agosto reparte sus noches entre ambos). Reservas, facturación, ADR y cancelación van por el mes de la entrada."
          extra={<Conmutador opciones={METRICAS} valor={metrica} onChange={setMetrica} />}
        >
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={porMes} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={tema.grid} />
              <XAxis dataKey="mes" tick={{ fontSize: 12, fill: tema.axis }} />
              <YAxis
                tick={{ fontSize: 12, fill: tema.axis }}
                tickFormatter={(v) => (metrica === 'facturacion' && v >= 1000 ? `${(v / 1000).toFixed(0)}k` : metrica === 'tasaCancelacion' ? `${v}%` : String(v))}
              />
              <Tooltip formatter={(v: any) => [formato(metrica, Number(v)), METRICAS.find((m) => m.valor === metrica)?.label]} cursor={{ fill: tema.cursor }} contentStyle={tema.tooltip} />
              <Bar dataKey="valor" fill={tema.bar} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Tarjeta>

        <Tarjeta
          titulo="Día de la semana"
          info="Entradas y salidas por el día de la semana en que ocurren, y noches dormidas por día de la semana. Sirve para ver el peso del fin de semana y qué días concentran las limpiezas."
        >
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={diaSemana} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={tema.grid} />
              <XAxis dataKey="dia" tick={{ fontSize: 12, fill: tema.axis }} />
              <YAxis tick={{ fontSize: 12, fill: tema.axis }} />
              <Tooltip formatter={(v: any) => fmtNum(Number(v))} cursor={{ fill: tema.cursor }} contentStyle={tema.tooltip} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Entradas" fill={tema.bar} radius={[4, 4, 0, 0]} />
              <Bar dataKey="Salidas" fill={tema.bar2} radius={[4, 4, 0, 0]} />
              <Bar dataKey="Noches" fill={tema.bar3} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Tarjeta>
      </div>

      <Tarjeta
        titulo="Mapa de calor por año y mes"
        info="Cada celda es un mes de un año. Las noches se cuentan en el mes en que se duermen; las reservas, en el mes de la entrada. El color va del mínimo al máximo de toda la tabla."
        className="mb-6"
        extra={
          <Conmutador
            opciones={[
              { valor: 'noches', label: 'Noches' },
              { valor: 'reservas', label: 'Reservas' },
            ]}
            valor={calor}
            onChange={setCalor}
          />
        }
      >
        <div className="overflow-x-auto -mx-6 px-6">
          <table className="w-full text-xs whitespace-nowrap">
            <thead>
              <tr className="text-sereno-dark/50">
                <th className="py-1.5 pr-2 text-left font-medium">Año</th>
                {MESES_CORTOS.map((m) => (
                  <th key={m} className="py-1.5 px-1 text-center font-medium">{m}</th>
                ))}
                <th className="py-1.5 pl-2 text-right font-medium">Total</th>
              </tr>
            </thead>
            <tbody>
              {data.mapaCalor.map((fila) => {
                const valores = fila[calor];
                const total = valores.reduce((s, v) => s + v, 0);
                return (
                  <tr key={fila.anio}>
                    <td className="py-1 pr-2 font-semibold">{fila.anio}</td>
                    {valores.map((v, i) => {
                      const intensidad = v / maxCalor;
                      return (
                        <td key={i} className="p-0.5">
                          <div
                            className="rounded px-1 py-1.5 text-center tabular-nums"
                            style={{
                              backgroundColor: isDark ? `rgba(209, 224, 240, ${0.06 + intensidad * 0.8})` : `rgba(11, 29, 46, ${0.04 + intensidad * 0.85})`,
                              color: intensidad > 0.45 ? (isDark ? '#0B1D2E' : '#fff') : undefined,
                            }}
                            title={`${MESES_CORTOS[i]} ${fila.anio}: ${fmtNum(v)}`}
                          >
                            {v ? fmtNum(v) : '·'}
                          </div>
                        </td>
                      );
                    })}
                    <td className="py-1 pl-2 text-right font-semibold tabular-nums">{fmtNum(total)}</td>
                  </tr>
                );
              })}
              {data.mapaCalor.length === 0 && (
                <tr>
                  <td colSpan={14} className="py-4 text-center text-sereno-dark/40">Sin datos</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Tarjeta>
    </>
  );
}
